import React, { useMemo } from 'react';
import { createContextualCan } from '@casl/react';

import { defineAbility } from '@casl/ability';
import { useSuspenseQuery } from '@tanstack/react-query';
import {
  fetchAuditTrailsPermissions,
  fetchBulkProvisioninPermissions,
  fetchSimInventoryPermissions,
} from './api/permissionsApi';
import {
  createAuditTrailsPermissionsFromRules,
  createBulkProvisioningPermissionsFromRules,
  createSimInventoryPermissionsFromRules,
} from './ability';
import {
  AuditTrailsPermissions,
  BulkProvisioningPermissions,
  SimInventoryPermissions,
} from './Permissions.model';
import { AbilityContext } from 'src/permissions/AbilityContext';

export const PermissionProvider = ({ children }: { children?: React.ReactNode }) => {
  const { data: permissions, isFetched } = useSuspenseQuery({
    queryKey: ['permissions'],
    queryFn: async () => {
      let simInventoryPermissions: SimInventoryPermissions[] = [];
      try {
        simInventoryPermissions = await fetchSimInventoryPermissions();
      } catch {
        simInventoryPermissions = [];
      }

      let bulkPermissions: BulkProvisioningPermissions[] = [];
      try {
        bulkPermissions = await fetchBulkProvisioninPermissions();
      } catch {
        bulkPermissions = [];
      }

      let auditTrails: AuditTrailsPermissions[] = [];
      try {
        auditTrails = await fetchAuditTrailsPermissions();
      } catch {
        auditTrails = [];
      }

      return {
        simInventoryPermissions,
        bulkPermissions,
        auditTrails,
      };
    },
  });

  const ability = useMemo(() => {
    if (permissions) {
      return defineAbility((can) => {
        createSimInventoryPermissionsFromRules(permissions.simInventoryPermissions, can);
        createBulkProvisioningPermissionsFromRules(permissions.bulkPermissions, can);
        createAuditTrailsPermissionsFromRules(permissions.auditTrails, can);
      });
    } else {
      return defineAbility(() => {});
    }
  }, [permissions]);

  return isFetched ? (
    <AbilityContext.Provider value={ability}>{children}</AbilityContext.Provider>
  ) : null;
};

export const SimInventoryCan = createContextualCan(AbilityContext.Consumer);
